import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Send, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailRecommendModal = ({ show, user, onClose }) => {
  const [friendEmail, setFriendEmail] = useState('');
  const [friendName, setFriendName] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendRecommendation = async () => {
    if (!friendEmail.trim()) {
      toast.error('Please enter your friend\'s email');
      return;
    }

    if (!validateEmail(friendEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/shows/recommend-email`,
        {
          friend_email: friendEmail.trim(),
          friend_name: friendName.trim() || null,
          show_title: show.title,
          show_image_url: show.image_url,
          show_overview: show.overview || null,
          tmdb_id: show.tmdb_id,
          message: message.trim() || null
        },
        { withCredentials: true }
      );

      toast.success(`Recommendation sent to ${friendName || friendEmail}!`);
      onClose();
    } catch (error) {
      console.error('Error sending email recommendation:', error);
      toast.error(error.response?.data?.detail || 'Error sending recommendation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="email-recommend-modal" data-testid="email-recommend-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            <Mail size={20} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
            Recommend "{show.title}" via Email
          </DialogTitle>
          <p id="email-recommend-description" className="sr-only">Send a show recommendation to a friend via email</p>
        </DialogHeader>

        <div className="modal-body">
          <div className="show-preview">
            {show.image_url && (
              <img src={show.image_url} alt={show.title} className="show-poster" />
            )}
            <div className="show-info">
              <div className="show-title">{show.title}</div>
              {show.overview && (
                <div className="show-overview">{show.overview.substring(0, 100)}...</div>
              )}
            </div>
          </div>

          <div className="section">
            <label className="section-label">Friend's Email *</label>
            <Input
              type="email"
              value={friendEmail}
              onChange={(e) => setFriendEmail(e.target.value)}
              placeholder="friend@example.com"
              className="email-input"
              data-testid="friend-email-input"
            />
          </div>

          <div className="section">
            <label className="section-label">Friend's Name (optional)</label>
            <Input
              value={friendName}
              onChange={(e) => setFriendName(e.target.value)}
              placeholder="Their name for a personal touch"
              className="name-input"
              data-testid="friend-name-input"
            />
          </div>

          <div className="section">
            <label className="section-label">Personal Message (optional)</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell them why they'll love this show..."
              rows={3}
              maxLength={300}
              className="message-input"
              data-testid="message-input"
            />
            <div className="char-count">{message.length}/300</div>
          </div>

          <div className="info-note">
            <p>Your friend will receive an email with your recommendation and an invitation to join Baad.tv!</p>
          </div>

          <div className="modal-actions">
            <Button
              onClick={handleSendRecommendation}
              disabled={loading || !friendEmail.trim()}
              className="send-btn"
              data-testid="send-email-recommend-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="spinning" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send Recommendation
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>

        <style jsx>{`
          .email-recommend-modal {
            max-width: 500px;
          }

          .email-recommend-modal :global(button[class*="absolute"]) {
            border: none !important;
            background: transparent !important;
            box-shadow: none !important;
          }

          .modal-body {
            padding: 1rem 0;
          }

          .show-preview {
            display: flex;
            gap: 1rem;
            padding: 1rem;
            background: #1a1a1a;
            border-radius: 8px;
            margin-bottom: 1.5rem;
          }

          .show-poster {
            width: 60px;
            height: 90px;
            object-fit: cover;
            border-radius: 4px;
            flex-shrink: 0;
          }

          .show-info {
            flex: 1;
            min-width: 0;
          }

          .show-title {
            color: #E62F88;
            font-weight: 600;
            font-size: 1rem;
            margin-bottom: 0.5rem;
          }

          .show-overview {
            color: #888;
            font-size: 0.85rem;
            line-height: 1.4;
          }

          .section {
            margin-bottom: 1.25rem;
          }

          .section-label {
            display: block;
            font-weight: 600;
            color: #ffffff;
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
          }

          .email-input,
          .name-input,
          .message-input {
            width: 100%;
            background: #1a1a1a;
            border: 1px solid #333;
            color: #ffffff;
          }

          .message-input {
            resize: vertical;
          }

          .char-count {
            text-align: right;
            font-size: 0.8rem;
            color: #666;
            margin-top: 0.25rem;
          }

          .info-note {
            padding: 0.75rem 1rem;
            background: rgba(230, 47, 136, 0.1);
            border: 1px solid rgba(230, 47, 136, 0.3);
            border-radius: 6px;
            margin-bottom: 1.5rem;
          }

          .info-note p {
            color: #b0b0b0;
            font-size: 0.85rem;
            margin: 0;
          }

          .modal-actions {
            display: flex;
            gap: 1rem;
            justify-content: flex-end;
          }

          .send-btn {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            background: #E62F88;
            color: #000000;
          }

          .send-btn:hover:not(:disabled) {
            background: #ff4da6;
          }

          .send-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .spinning {
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default EmailRecommendModal;
